import { useEffect, useState } from "react";
import { Download, Share, PlusSquare, X, Smartphone } from "lucide-react";

const isStandalone = () =>
    window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

// "Install app" button: native prompt on Android/desktop Chrome, step-by-step guide on iPhone/other browsers.
export const InstallAppButton = ({ variant = "dark", className = "" }) => {
    const [prompt, setPrompt] = useState(() => window.__installPrompt || null);
    const [installed, setInstalled] = useState(isStandalone());
    const [guide, setGuide] = useState(false);

    useEffect(() => {
        const ready = () => setPrompt(window.__installPrompt);
        const done = () => { setInstalled(true); window.__installPrompt = null; };
        window.addEventListener("installprompt:ready", ready);
        window.addEventListener("appinstalled", done);
        return () => { window.removeEventListener("installprompt:ready", ready); window.removeEventListener("appinstalled", done); };
    }, []);

    if (installed) return null;

    const onClick = async () => {
        if (prompt) {
            prompt.prompt();
            const { outcome } = await prompt.userChoice;
            if (outcome === "accepted") setInstalled(true);
            window.__installPrompt = null;
            setPrompt(null);
        } else {
            setGuide(true);
        }
    };

    const styles = variant === "dark"
        ? "bg-white/10 text-white hover:bg-white/20"
        : "border border-ink/15 bg-white text-ink hover:bg-ink/5";

    return (
        <>
            <button type="button" onClick={onClick} data-testid="install-app-button"
                className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${styles} ${className}`}>
                <Download className="w-4 h-4" strokeWidth={1.75} /> Install app
            </button>
            {guide && <InstallGuide onClose={() => setGuide(false)} />}
        </>
    );
};

function InstallGuide({ onClose }) {
    const ios = isIOS();
    const steps = ios
        ? [
            [Share, "Tap the Share button", "at the bottom of Safari (square with an arrow)"],
            [PlusSquare, "Choose \u201cAdd to Home Screen\u201d", "scroll down the list if you don't see it"],
            [Smartphone, "Tap \u201cAdd\u201d", "the app icon appears on your home screen"],
        ]
        : [
            [Share, "Open the browser menu", "the \u22ee or \u2026 button next to the address bar"],
            [PlusSquare, "Choose \u201cInstall app\u201d or \u201cAdd to Home screen\u201d", ""],
            [Smartphone, "Confirm", "the app icon appears on your home screen"],
        ];
    return (
        <div className="fixed inset-0 z-[200] bg-[#0E1630]/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="install-guide">
            <div className="bg-white w-full max-w-sm p-6 text-ink text-left" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                        <img src="/logo192.png" alt="" className="w-12 h-12 rounded-xl" />
                        <div>
                            <div className="font-display font-bold text-lg">Install Suntosh HRM</div>
                            <div className="text-xs text-ink/55">{ios ? "On iPhone or iPad (Safari)" : "On your phone or computer"}</div>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1 text-ink/50 hover:text-ink" data-testid="install-guide-close"><X className="w-5 h-5" /></button>
                </div>
                <ol className="mt-6 space-y-4">
                    {steps.map(([Icon, t, d], i) => (
                        <li key={t} className="flex gap-3 text-left" data-testid={`install-step-${i + 1}`}>
                            <div className="w-9 h-9 rounded-xl bg-klein-50 text-klein flex items-center justify-center shrink-0"><Icon className="w-4 h-4" /></div>
                            <div>
                                <div className="text-sm font-semibold">{i + 1}. {t}</div>
                                {d && <div className="text-xs text-ink/55">{d}</div>}
                            </div>
                        </li>
                    ))}
                </ol>
                {ios && <p className="mt-5 text-xs text-ink/50">Using Chrome on iPhone? Open this page in Safari first.</p>}
            </div>
        </div>
    );
}

export default InstallAppButton;
